import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import { Box, Button, CircularProgress, Fab, IconButton, Popover, Typography } from '@mui/material'
import { useState, useRef } from 'react'
import { useScreenSize } from 'src/customHooks/customHooks'

const POPOVER_SAME_CLICK_IGNORE_MS = 200

export default function LeaveButton({
  leaveSession,
  isAlreadyMarkedAsCompleted = false,
  isLeavingSession = false,
  customButton = null,
  // Lets callers lift the whole popover (its Modal root) above stacking contexts such as the
  // mobile chamber's full-screen report sheet (z-index 1500) / PIP video (1700).
  popoverZIndex = undefined,
  children,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const [markingSessionAsCompleted, setMarkingSessionAsCompleted] = useState(false)
  const openTimeRef = useRef(0)
  const { width } = useScreenSize()

  const handleClick = (event) => {
    if (isLeavingSession) return
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    openTimeRef.current = Date.now()
  }

  const isMobile = width < 768

  const handleClose = (event, reason) => {
    // Prevent same-click close: the click that opens the popover is often treated as backdropClick
    // and closes it immediately, so the user never sees the popover. Ignore backdropClick for a short window.
    if (reason === 'backdropClick' && Date.now() - openTimeRef.current < POPOVER_SAME_CLICK_IGNORE_MS) {
      return
    }
    setAnchorEl(null)
  }

  const defaultButton = !isMobile ? (
    <Button
      type="button"
      size="small"
      variant="contained"
      disabled={isLeavingSession}
      endIcon={isLeavingSession ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <ExitToAppIcon />}
      sx={{ 
        borderRadius: '12px', 
        textTransform: 'none', 
        fontSize: '14px',
        fontWeight: 600,
        backgroundColor: '#ef4444',
        color: '#ffffff',
        border: '1px solid #dc2626',
        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
        padding: '8px 20px',
        '&:hover': {
          backgroundColor: '#dc2626',
          borderColor: '#b91c1c',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
        },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onClick={handleClick}
    >
      {isLeavingSession ? 'Leaving...' : (children || 'Leave')}
    </Button>
  ) : (
    <Fab
      type="button"
      size="small"
      onClick={handleClick}
      disabled={isLeavingSession}
      sx={{
        backgroundColor: '#ef4444',
        color: '#ffffff',
        border: '1px solid #dc2626',
        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
        width: '44px',
        height: '44px',
        '&:hover': {
          backgroundColor: '#dc2626',
          borderColor: '#b91c1c',
          transform: 'scale(1.1)',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
        },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {isLeavingSession ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : <ExitToAppIcon />}
    </Fab>
  )

  return (
    <div>
      {/* Single click target: wrapper only when customButton, else the button itself handles click (avoids double-fire) */}
      <div onClick={customButton ? handleClick : undefined}>{customButton || defaultButton}</div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(event, reason) => handleClose(event, reason)}
        sx={popoverZIndex ? { zIndex: popoverZIndex } : undefined}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            zIndex: 1400,
            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2), 0 6px 20px rgba(118, 75, 162, 0.15)',
            borderRadius: '16px',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: 2.5, minWidth: '280px', maxWidth: '320px' }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600, 
              mb: 2, 
              color: '#1e293b',
              fontSize: '16px',
              letterSpacing: '-0.01em',
            }}
          >
            How would you like to end this session?
          </Typography>

          {/* Keep HCIP buttons for backward compatibility */}
          {/* <HCIPMenuItem
            style={{ background: 'green', color: '#fff', marginBottom: '8px' }}
            onClick={() => {
              markSessionAsCompleted()
              handleClose()
            }}
          >
            Mark Complete
          </HCIPMenuItem>
          <HCIPMenuItem 
            endVariant 
            style={{ marginBottom: '8px' }}
            onClick={() => {
              leaveSession()
              handleClose()
            }}
          >
            Save and Go Back
          </HCIPMenuItem> */}

          {/* Save Draft & Come Back Later - Only show when NOT already completed */}
          {!isAlreadyMarkedAsCompleted && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PauseCircleOutlineIcon />}
              disabled={markingSessionAsCompleted}
              sx={{
                mb: 1.5,
                py: 1.4,
                textAlign: 'left',
                justifyContent: 'flex-start',
                borderColor: 'rgba(148, 163, 184, 0.3)',
                color: '#64748b',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '15px',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.08)',
                  borderColor: 'rgba(102, 126, 234, 0.4)',
                  color: '#475569',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(102, 126, 234, 0.15)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onClick={async () => {
                setMarkingSessionAsCompleted(true)
                // "Save Draft & Come Back Later" should save as draft (markAsCompleted = false)
                await leaveSession(false)
                setMarkingSessionAsCompleted(false)
                handleClose()
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                  Save Draft & Come Back Later
                </Typography>
              </Box>
            </Button>
          )}

          {/* Save & Leave Session - Only show when already completed */}
          {isAlreadyMarkedAsCompleted && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ExitToAppIcon />}
              disabled={markingSessionAsCompleted}
              sx={{
                mb: 1.5,
                py: 1.4,
                textAlign: 'left',
                justifyContent: 'flex-start',
                borderColor: 'rgba(148, 163, 184, 0.3)',
                color: '#64748b',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '15px',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.08)',
                  borderColor: 'rgba(102, 126, 234, 0.4)',
                  color: '#475569',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(102, 126, 234, 0.15)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onClick={async () => {
                setMarkingSessionAsCompleted(true)
                // "Save & Leave Session" should save as completed (markAsCompleted = true)
                await leaveSession(true)
                setMarkingSessionAsCompleted(false)
                handleClose()
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                  Save & Leave Session
                </Typography>
              </Box>
            </Button>
          )}

          {/* Save & Complete This Session - Only show when NOT already completed */}
          {!isAlreadyMarkedAsCompleted && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              disabled={markingSessionAsCompleted}
              sx={{
                py: 1.4,
                textAlign: 'left',
                justifyContent: 'flex-start',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3), 0 2px 6px rgba(118, 75, 162, 0.2)',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '15px',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4), 0 3px 8px rgba(118, 75, 162, 0.3)',
                },
                '&:disabled': {
                  background: 'rgba(148, 163, 184, 0.3)',
                  color: 'rgba(100, 116, 139, 0.6)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onClick={async () => {
                setMarkingSessionAsCompleted(true)
                try {
                  await leaveSession(true)
                } catch (error) {
                  console.error('Error in leaveSession:', error)
                  // Show error to user
                  alert('Error completing session: ' + (error?.message || 'Unknown error'))
                } finally {
                  setMarkingSessionAsCompleted(false)
                  handleClose()
                }
              }}
            >
              {markingSessionAsCompleted ? (
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                    Saving...
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'white' }}>
                    Save & Complete This Session
                  </Typography>
                </Box>
              )}
            </Button>
          )}
        </Box>
      </Popover>
    </div>
  )
}
